import { Component } from '@theme/component';

/**
 * @typedef {Object} JudgeMeReview
 * @property {number} id
 * @property {string|null} title
 * @property {string} body
 * @property {number} rating
 * @property {{ name: string }} reviewer
 * @property {string} verified
 * @property {string} created_at
 * @property {boolean} has_published_pictures
 * @property {{ urls: { small: string, compact: string, huge: string, original: string } }[]} pictures
 */

/**
 * @typedef {Object} ReviewsRefs
 * @property {HTMLElement} reviewsList
 * @property {HTMLElement} skeleton
 * @property {HTMLElement} loadMoreWrapper
 * @property {HTMLButtonElement} loadMoreBtn
 * @property {HTMLElement} loadMoreLabel
 * @property {HTMLElement} emptyState
 * @property {HTMLElement} errorState
 * @property {HTMLElement[]} breakdownBars
 * @property {HTMLElement[]} breakdownCounts
 * @property {HTMLButtonElement[]} filterBtns
 * @property {HTMLElement} summarySection
 * @property {HTMLElement} summaryLabel
 * @property {HTMLElement} reviewCountLabel
 */

/**
 * Aromely PDP Reviews section powered by Judge.me API via edge proxy.
 * @extends {Component<ReviewsRefs>}
 */
class AromelyPdpReviews extends Component {
  /** @type {number[]} */
  #jmProductIds = [];

  /** @type {number} */
  #currentPage = 1;

  /** @type {number} */
  #activeFilter = 0;

  /** @type {number} */
  #defaultMinRating = 0;

  /** @type {number} */
  #perPage = 5;

  /** @type {string} */
  #proxyBase = '';

  /** @type {string[]} */
  #groupExternalIds = [];

  /** @type {number} */
  #totalCount = 0;

  /** @type {Record<number, number>} */
  #breakdownCounts = {};

  /** @type {boolean} */
  #showPhotos = true;

  /** @type {boolean} */
  #hasMore = false;

  /** @type {AbortController} */
  #abortController;

  /** @type {IntersectionObserver|null} */
  #observer = null;

  /** @type {HTMLDialogElement|null} */
  #lightbox = null;

  /** @type {MediaQueryList} */
  #desktopMq = window.matchMedia('(min-width: 1024px)');

  /** @type {Record<string, string>} */
  #t = {};

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    this.#proxyBase = this.dataset.proxyBase || '';
    const groupRaw = this.dataset.groupIds || this.dataset.productId || '';
    this.#groupExternalIds = groupRaw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    this.#perPage = parseInt(this.dataset.perPage || '5', 10);
    this.#totalCount = parseInt(this.dataset.ratingCount || '0', 10);
    this.#showPhotos = this.dataset.showPhotos !== 'false';
    this.#defaultMinRating = parseInt(this.dataset.defaultMinRating || '0', 10);

    // Load translations from data attributes
    this.#t = {
      reviewCount: this.dataset.tReviewCount || '__COUNT__ reviews',
      noStarReviews: this.dataset.tNoStarReviews || 'No __STAR__-star reviews yet.',
      noTopReviews: this.dataset.tNoTopReviews || 'No reviews with __MIN__+ stars yet.',
      anonymous: this.dataset.tAnonymous || 'Anonymous',
      verified: this.dataset.tVerified || 'Verified',
      ratingAria: this.dataset.tRatingAria || 'Rating: __RATING__ out of 5',
      reviewPhoto: this.dataset.tReviewPhoto || 'Review photo',
      close: this.dataset.tClose || 'Close',
    };

    this.#updateFilterUI();

    // Lazy-load: only fetch when section is near the viewport
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this.#observer?.disconnect();
          this.#observer = null;
          this.#init().catch(() => {
            this.#hideSkeleton();
            this.#showError();
          });
        }
      },
      { rootMargin: '400px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#observer?.disconnect();
  }

  /**
   * Filter reviews by star rating.
   * @param {Event} event
   */
  handleFilter(event) {
    const btn = /** @type {HTMLButtonElement} */ (event.target).closest('[data-rating]');
    if (!btn) return;
    const rating = parseInt(btn.dataset.rating || '0', 10);

    // Toggle: clicking same filter clears it back to the default state
    if (this.#activeFilter === rating && rating !== 0) {
      this.#activeFilter = 0;
    } else {
      this.#activeFilter = rating;
    }

    this.#currentPage = 1;
    this.#updateFilterUI();
    this.#loadReviews(false);
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /** Sync filter button states (aria, opacity, clear-button visibility) with current filter. */
  #updateFilterUI() {
    for (const b of this.refs.filterBtns || []) {
      const r = parseInt(b.dataset.rating || '0', 10);

      if (r === 0) {
        // "Clear filter" button — only useful when an exact-rating filter is active
        b.setAttribute('aria-checked', String(this.#activeFilter === 0));
        b.classList.toggle('hidden', this.#activeFilter === 0);
        continue;
      }

      const isActive = r === this.#activeFilter;
      b.setAttribute('aria-checked', String(isActive));

      let dim;
      if (this.#activeFilter > 0) {
        // Exact filter: highlight the active star, dim the rest
        dim = !isActive;
      } else if (this.#defaultMinRating > 0) {
        // Default hide-low state: dim ratings below the threshold
        dim = r < this.#defaultMinRating;
      } else {
        // Show-all default: nothing dimmed
        dim = false;
      }
      b.style.opacity = dim ? '0.4' : '1';
    }
  }

  /** Load more reviews (append). */
  handleLoadMore() {
    this.#currentPage++;
    this.#loadReviews(true);
  }

  /**
   * Open photo lightbox.
   * @param {Event} event
   */
  handlePhotoClick(event) {
    event.preventDefault();
    const btn = /** @type {HTMLElement} */ (event.target).closest('[data-photo-url]');
    if (!btn) return;

    const url = btn.getAttribute('data-photo-url') || '';
    if (!url) return;

    if (!this.#lightbox) {
      this.#lightbox = document.createElement('dialog');
      this.#lightbox.className = 'aromely-reviews__lightbox';
      this.#lightbox.setAttribute('aria-label', this.#t.reviewPhoto);
      this.#lightbox.innerHTML = `
        <button class="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 text-[#0c0a09] z-10" aria-label="${this.#t.close}">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
        <img class="aromely-reviews__lightbox-img" alt="${this.#t.reviewPhoto}" />
      `;
      this.#lightbox.addEventListener('click', (e) => {
        if (e.target === this.#lightbox || /** @type {HTMLElement} */ (e.target).closest('button')) {
          this.#lightbox?.close();
        }
      });
      this.appendChild(this.#lightbox);
    }

    const img = this.#lightbox.querySelector('img');
    if (img) img.src = url;
    this.#lightbox.showModal();
  }

  /** Retry after error. */
  handleRetry() {
    this.#init().catch(() => {
      this.#hideSkeleton();
      this.#showError();
    });
  }

  async #init() {
    if (!this.#proxyBase || this.#groupExternalIds.length === 0) {
      this.#hideSkeleton();
      return;
    }

    this.#showSkeleton();
    this.#hideError();

    const productIds = await this.#resolveProductIds();
    if (productIds.length === 0) {
      this.#hideSkeleton();
      this.#showEmpty();
      return;
    }

    this.#jmProductIds = productIds;

    try {
      await Promise.all([
        this.#loadReviews(false),
        this.#fetchBreakdown(),
      ]);
    } catch {
      this.#hideSkeleton();
      this.#showError();
    }
  }

  /**
   * Resolve every Shopify product id in the group to its Judge.me internal id.
   * Missing resolves are dropped silently.
   * @returns {Promise<number[]>}
   */
  async #resolveProductIds() {
    const results = await Promise.all(
      this.#groupExternalIds.map((id) =>
        this.#fetch(`${this.#proxyBase}/api/products?external_id=${id}`),
      ),
    );
    return results
      .map((data) => data?.product?.id)
      .filter((id) => id != null);
  }

  /**
   * Load reviews — replace or append.
   * @param {boolean} append
   */
  async #loadReviews(append) {
    if (!append) {
      this.#showSkeleton();
      this.#hideError();
    }

    const reviews = await this.#fetchReviews();
    if (!reviews) {
      this.#hideSkeleton();
      if (!append) this.#showError();
      return;
    }

    this.#hideSkeleton();
    this.#hasMore = reviews.length >= this.#perPage;

    if (reviews.length === 0 && !append && this.#currentPage === 1) {
      this.#hide(this.refs.loadMoreWrapper);
      if (this.#activeFilter > 0 || this.#defaultMinRating > 0) {
        this.#renderReviews([], false);
      } else {
        this.#showEmpty();
      }
      return;
    }

    this.#hideEmpty();
    this.#renderReviews(reviews, append);
    this.#updateLoadMore();
  }

  /**
   * Fetch reviews for current page and filter.
   * @returns {Promise<JudgeMeReview[]|null>}
   */
  async #fetchReviews() {
    let url = `${this.#proxyBase}/api/reviews?product_ids=${this.#jmProductIds.join(',')}&per_page=${this.#perPage}&page=${this.#currentPage}`;
    if (this.#activeFilter > 0) {
      url += `&rating=${this.#activeFilter}`;
    } else if (this.#defaultMinRating > 0) {
      url += `&rating_min=${this.#defaultMinRating}`;
    }

    const data = await this.#fetch(url);
    if (!data) return null;

    return data.reviews || [];
  }

  /** Fetch all star breakdown counts in a single request. */
  async #fetchBreakdown() {
    const data = await this.#fetch(`${this.#proxyBase}/api/reviews/count?product_ids=${this.#jmProductIds.join(',')}`);
    if (!data) return;

    this.#breakdownCounts = data.breakdown || {};
    if (data.total > 0) this.#totalCount = data.total;

    if (this.refs.reviewCountLabel) {
      this.refs.reviewCountLabel.textContent = this.#t.reviewCount.replace('__COUNT__', String(this.#totalCount));
    }

    this.#renderBreakdown();
  }

  /**
   * Render review cards.
   * @param {JudgeMeReview[]} reviews
   * @param {boolean} append
   */
  #renderReviews(reviews, append) {
    if (!this.refs.reviewsList) return;

    if (reviews.length === 0 && !append) {
      if (this.#activeFilter > 0) {
        this.refs.reviewsList.innerHTML = `
          <p class="text-sm text-[#78716c] text-center py-8">${this.#t.noStarReviews.replace('__STAR__', String(this.#activeFilter))}</p>
        `;
      } else if (this.#defaultMinRating > 0) {
        this.refs.reviewsList.innerHTML = `
          <p class="text-sm text-[#78716c] text-center py-8">${this.#t.noTopReviews.replace('__MIN__', String(this.#defaultMinRating))}</p>
        `;
      }
      this.#show(this.refs.reviewsList);
      return;
    }

    const cards = reviews.map((r) => this.#buildReviewCard(r));

    if (this.#desktopMq.matches) {
      this.#renderMasonry(cards, append);
    } else {
      const html = cards.join('');
      if (append) {
        this.refs.reviewsList.insertAdjacentHTML('beforeend', html);
      } else {
        this.refs.reviewsList.innerHTML = html;
      }
    }
    this.#show(this.refs.reviewsList);
  }

  /**
   * Distribute cards into a 2-column masonry layout, appending each card
   * to whichever column is currently shorter.
   * @param {string[]} cards - HTML strings for each card
   * @param {boolean} append
   */
  #renderMasonry(cards, append) {
    const list = this.refs.reviewsList;

    /** @type {HTMLElement[]} */
    let cols = /** @type {HTMLElement[]} */ ([...list.querySelectorAll('.aromely-reviews__masonry-col')]);

    if (!append || cols.length !== 2) {
      list.innerHTML = `
        <div class="aromely-reviews__masonry">
          <div class="aromely-reviews__masonry-col"></div>
          <div class="aromely-reviews__masonry-col"></div>
        </div>
      `;
      cols = /** @type {HTMLElement[]} */ ([...list.querySelectorAll('.aromely-reviews__masonry-col')]);
    }

    // Must be visible so offsetHeight is measurable
    this.#show(list);

    for (const cardHtml of cards) {
      const shorter = cols[0].offsetHeight <= cols[1].offsetHeight ? cols[0] : cols[1];
      shorter.insertAdjacentHTML('beforeend', cardHtml);
      // Force reflow so the next offsetHeight read reflects the new card
      shorter.offsetHeight;
    }
  }

  /** Render star breakdown bar chart. */
  #renderBreakdown() {
    const bars = this.refs.breakdownBars || [];
    const counts = this.refs.breakdownCounts || [];
    const total = this.#totalCount || 1;

    for (let i = 0; i < bars.length; i++) {
      const star = 5 - i;
      const count = this.#breakdownCounts[star] || 0;
      const pct = Math.round((count / total) * 100);
      /** @type {HTMLElement} */ (bars[i]).style.width = `${pct}%`;
    }

    for (let i = 0; i < counts.length; i++) {
      const star = 5 - i;
      /** @type {HTMLElement} */ (counts[i]).textContent = String(this.#breakdownCounts[star] || 0);
    }
  }

  /** Update load more button visibility. */
  #updateLoadMore() {
    if (this.#hasMore) {
      this.#show(this.refs.loadMoreWrapper);
    } else {
      this.#hide(this.refs.loadMoreWrapper);
    }
  }

  /**
   * Build HTML for a single review card.
   * Card order matches design: Name+Date → Body → Stars+Badge
   * @param {JudgeMeReview} review
   * @returns {string}
   */
  #buildReviewCard(review) {
    const name = this.#sanitize(review.reviewer?.name || this.#t.anonymous);
    const date = this.#formatDate(review.created_at);
    const stars = this.#buildStars(review.rating);
    const isVerified = (review.verified || '').toLowerCase().includes('buyer') ||
                       (review.verified || '').toLowerCase().includes('purchase');
    const body = review.body ? `<p class="text-sm text-[#78716c] leading-relaxed">${this.#sanitize(review.body)}</p>` : '';

    let photos = '';
    if (this.#showPhotos && review.has_published_pictures && review.pictures?.length) {
      const imgs = review.pictures.map((pic) => {
        const compact = pic.urls?.compact || pic.urls?.small || '';
        const huge = pic.urls?.huge || pic.urls?.original || compact;
        return `<button on:click="/handlePhotoClick" data-photo-url="${this.#sanitize(huge)}" class="shrink-0 rounded-lg overflow-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0c0a09]">
          <img src="${this.#sanitize(compact)}" loading="lazy" width="64" height="64" class="w-16 h-16 object-cover" alt="${this.#t.reviewPhoto}" />
        </button>`;
      }).join('');
      photos = `<div class="flex gap-2 overflow-x-auto">${imgs}</div>`;
    }

    const verifiedBadge = isVerified
      ? `<div class="flex items-center gap-1 text-[11px] font-medium text-[#16a34a] bg-[#f0fdf4] rounded px-2 py-1">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>
          ${this.#t.verified}
        </div>`
      : '';

    return `<article class="aromely-reviews__card">
      <div class="flex items-start justify-between gap-3">
        <div class="flex flex-col gap-1">
          <p class="text-sm font-medium text-[#0c0a09]">${name}</p>
          <p class="text-[11px] text-[#78716c]">${date}</p>
        </div>
      </div>
      ${body}
      ${photos}
      <div class="flex items-center justify-between gap-3">
        <div class="flex items-center gap-1" aria-label="${this.#t.ratingAria.replace('__RATING__', String(review.rating))}">${stars}</div>
        ${verifiedBadge}
      </div>
    </article>`;
  }

  /**
   * Build 5-star SVG string.
   * @param {number} rating
   * @returns {string}
   */
  #buildStars(rating) {
    const star = 'M2.20982 10.88184c-0.12533-0.0931-0.20231-0.21663-0.23096-0.37061-0.02506-0.15397-0.00179-0.33301 0.06983-0.53711l1.03662-3.08301-2.64258-1.89599c-0.17904-0.12891-0.30615-0.26318-0.38135-0.40283-0.07161-0.13965-0.08057-0.28467-0.02685-0.43506 0.05013-0.14681 0.14502-0.25602 0.28467-0.32764 0.13965-0.07161 0.31689-0.10563 0.53173-0.10205l3.24952 0.02148 0.98291-3.10449c0.06803-0.20768 0.15755-0.36702 0.26855-0.47803 0.111-0.111 0.24349-0.1665 0.39746-0.1665 0.15755 0 0.29183 0.0555 0.40283 0.1665 0.111 0.111 0.20052 0.27035 0.26856 0.47803l0.98828 3.10449 3.24414-0.02148c0.21484-0.00358 0.39209 0.03044 0.53174 0.10205 0.14323 0.07161 0.23812 0.18083 0.28467 0.32764 0.05371 0.15039 0.04297 0.29541-0.03223 0.43506-0.07161 0.13965-0.19515 0.27393-0.3706 0.40283l-2.64795 1.89599 1.03662 3.08301c0.07161 0.2041 0.0931 0.38314 0.06445 0.53711-0.02507 0.15397-0.10026 0.27751-0.22559 0.37061-0.12175 0.10026-0.26139 0.13607-0.41894 0.10742-0.15397-0.02507-0.32048-0.10384-0.49951-0.23633l-2.62647-1.92822-2.62109 1.92822c-0.17546 0.13249-0.34196 0.21126-0.49951 0.23633-0.15397 0.02865-0.29362-0.00716-0.41895-0.10742z';
    let html = '';
    for (let i = 1; i <= 5; i++) {
      if (rating >= i) {
        html += `<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path fill="#FFA41C" stroke="#FFA41C" stroke-width="0.6" d="${star}"/></svg>`;
      } else {
        html += `<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path fill="#ffd49a" stroke="#ffd49a" stroke-width="0.6" d="${star}"/></svg>`;
      }
    }
    return html;
  }

  /**
   * Format ISO date to readable string.
   * @param {string} dateStr
   * @returns {string}
   */
  #formatDate(dateStr) {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '';
    }
  }

  /**
   * Escape HTML entities.
   * @param {string} str
   * @returns {string}
   */
  #sanitize(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Fetch JSON from proxy with abort signal.
   * @param {string} url
   * @returns {Promise<any|null>}
   */
  async #fetch(url) {
    try {
      const timeout = AbortSignal.timeout(8000);
      const response = await fetch(url, {
        signal: AbortSignal.any([this.#abortController.signal, timeout]),
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') return null;
      if (/** @type {Error} */ (error).name === 'TimeoutError') return null;
      console.error('Reviews API error:', error);
      return null;
    }
  }

  /** @param {HTMLElement|undefined} el */
  #show(el) {
    if (el) el.classList.remove('hidden');
  }

  /** @param {HTMLElement|undefined} el */
  #hide(el) {
    if (el) el.classList.add('hidden');
  }

  #showSkeleton() {
    this.#show(this.refs.skeleton);
    this.#hide(this.refs.reviewsList);
    this.#hide(this.refs.loadMoreWrapper);
    this.#hide(this.refs.emptyState);
    this.#hide(this.refs.errorState);
  }

  #hideSkeleton() {
    this.#hide(this.refs.skeleton);
  }

  #showEmpty() {
    this.#show(this.refs.emptyState);
    this.#hide(this.refs.reviewsList);
    this.#hide(this.refs.loadMoreWrapper);
    this.#hide(this.refs.errorState);
  }

  #hideEmpty() {
    this.#hide(this.refs.emptyState);
  }

  #showError() {
    this.#show(this.refs.errorState);
    this.#hide(this.refs.reviewsList);
    this.#hide(this.refs.emptyState);
    this.#hide(this.refs.loadMoreWrapper);
  }

  #hideError() {
    this.#hide(this.refs.errorState);
  }
}

if (!customElements.get('aromely-pdp-reviews')) {
  customElements.define('aromely-pdp-reviews', AromelyPdpReviews);
}
